import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Scale } from 'lucide-react';

const navLinks = [
  { path: '/', label: 'Home' },
  { path: '/practice-areas', label: 'Practice Areas' },
  { path: '/consultation', label: 'Book Consultation' },
  { path: '/portal', label: 'Client Portal' },
  { path: '/resources', label: 'Resources' },
  { path: '/contact', label: 'Contact' },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setIsOpen(false); }, [location.pathname]);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled
        ? 'bg-[#0a1628]/95 backdrop-blur-xl border-b border-[#c9a84c]/10 shadow-2xl'
        : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 bg-gradient-to-br from-[#c9a84c] to-[#b08e2e] rounded-lg flex items-center justify-center shadow-lg group-hover:shadow-[#c9a84c]/30 transition-shadow duration-300">
              <Scale className="w-5 h-5 text-[#0a1628]" />
            </div>
            <div>
              <span className="text-lg font-bold tracking-wide" style={{ fontFamily: 'Playfair Display, serif' }}>
                Summit <span className="text-[#c9a84c]">Legal</span>
              </span>
              <div className="text-[10px] tracking-[0.2em] text-[#c9a84c]/70 uppercase">Attorneys at Law</div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 ${
                  location.pathname === link.path
                    ? 'text-[#c9a84c] bg-[#c9a84c]/10'
                    : 'text-[#e8e4dc]/70 hover:text-[#c9a84c] hover:bg-[#c9a84c]/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA + Mobile */}
          <div className="flex items-center gap-3">
            <a href="tel:+12017984100" className="hidden sm:flex items-center gap-2 text-sm text-[#c9a84c]/80 hover:text-[#c9a84c] transition-colors">
              <Phone className="w-4 h-4" />
              <span>(201) 798-4100</span>
            </a>
            <Link
              to="/consultation"
              className="hidden md:inline-flex px-5 py-2.5 bg-gradient-to-r from-[#c9a84c] to-[#b08e2e] text-[#0a1628] text-sm font-semibold rounded-lg hover:shadow-lg hover:shadow-[#c9a84c]/20 transition-all duration-300 hover:scale-105"
            >
              Free Consultation
            </Link>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 text-[#e8e4dc] hover:text-[#c9a84c] transition-colors"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#0a1628]/98 backdrop-blur-xl border-b border-[#c9a84c]/10 overflow-hidden"
          >
            <div className="px-4 py-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-4 py-3 text-sm font-medium rounded-lg transition-all ${
                    location.pathname === link.path
                      ? 'text-[#c9a84c] bg-[#c9a84c]/10'
                      : 'text-[#e8e4dc]/70 hover:text-[#c9a84c] hover:bg-[#c9a84c]/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/consultation"
                className="block px-4 py-3 bg-gradient-to-r from-[#c9a84c] to-[#b08e2e] text-[#0a1628] text-sm font-semibold rounded-lg text-center mt-3"
              >
                Free Consultation
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
